<template>
  <div class="max-w-3xl w-full mx-auto pb-3">
    <div class="flex flex-col items-stretch">

      <wizard ref="wizard" @changepage="$emit('changepage', $event)">
        <wizard-page :requires="[$v.displayName, $v.profile.birthyear, $v.profile.country, $v.profile.residence]">
          <text-input
            v-model.trim="displayName"
            autocomplete="nickname"
            :validation="$v.displayName"
            :placeholder="$t('form.nickname.placeholder')"
            :info="$t('displaynameinfo')"
            :errorcheckers="{
              minLength: (validation) => $t('error.form.minlength', {min: validation.$params.minLength.min}),
              required: (validation) => $t('error.form.required')
            }"
          />

          <text-input v-model="profile.givenname" autocomplete="given-name" :placeholder="$t('form.givenname.placeholder')" />
          <text-input v-model="profile.familyname" autocomplete="family-name" :placeholder="$t('form.familyname.placeholder')" />
          
          <integer-input
            v-model="profile.birthyear"
            autocomplete="bday-year"
            :validation="$v.profile.birthyear"
            :min="maxyear-120"
            :max="maxyear"
            :placeholder="$t('form.birthyear.placeholder')"
            :errorcheckers="{
              between: (validation) => $t('error.form.invalidField', {field: $t('form.birthyear.placeholder')}),
              required: (validation) => $t('error.form.required')
            }"
          />

          <!-- https://www.orientamento.ch/dyn/show/2800 -->
          <select-input 
            v-model="profile.degree"
            :placeholder="$t('form.degree.placeholder')"
            :options="{
              '1': $t('degrees.1'),
              '2': $t('degrees.2'),
              '3': $t('degrees.3'),
              '4': $t('degrees.4'),
              '5': $t('degrees.5'),
              '6': $t('degrees.6'),
              '7': $t('degrees.7')
            }"
          />

          <select-input
            v-model="profile.country"
            :validation="$v.profile.country"
            :placeholder="$t('form.country.placeholder')"
            :options="Object.fromEntries(countryCodes.map(cc => [cc, $t('countries.' + cc)]))"
            :errorcheckers="{
              required: (validation) => $t('error.form.required')
            }"
          />
        
          <template v-if="profile.country == 'CH' || profile.country == 'IT'">
            <place-selector
              v-model="profile.residence"
              :validation="$v.profile.residence"
              :country="profile.country"
              :placeholder="$t('form.residence.placeholder')"
            />

            <otherplaces-editor 
              v-model="profile.places"
              :residenceCountry="profile.country"
              :residenceRegion="profile.residence.region"
            />
          </template>
        </wizard-page>

        <wizard-page :requires="[$v.profile.mainLanguage, $v.profile.placeLanguage]">
          <p class="text-secondary_text mt-8 mb-4">
            {{$t('profile.question.main-language')}}*
          </p>
          <checks-input 
            otherPlaceholder="Altro"
            v-model="profile.mainLanguage"
            :validation="$v.profile.mainLanguage"
            :options="languages"
            :info="$t('multiplechoice-info')"
            :showOptions="5"
            :errorcheckers="{
              required: (validation) => $t('error.form.required')
            }"
          />

          <p class="text-secondary_text mt-8 mb-4">
            {{$t('profile.question.known-languages')}}
          </p>
          <checks-input
            otherPlaceholder="Altro"
            v-model="profile.otherLanguages"
            :options="languages"
            :info="$t('multiplechoice-info')"
            :showOptions="5"
          />

          <p class="text-secondary_text mt-8 mb-4">
            {{$t('profile.question.place-language')}}*
          </p>
          <checks-input 
            otherPlaceholder="Altro"
            v-model="profile.placeLanguage"
            :validation="$v.profile.placeLanguage"
            :options="languages"
            :info="$t('multiplechoice-info')"
            :showOptions="5"
            :ignoreOptions="['lmo','gsw']"
            :errorcheckers="{
              required: (validation) => $t('error.form.required')
            }"
          />
        </wizard-page>

        <wizard-page :requires="[profile.italianOriginCountry, profile.italianOriginCity, $v.profile.languageContext]">
          <p class="text-secondary_text mt-8 mb-4">{{$t('profile.question.italian-origin')}}*</p>
          <select-input 
            v-model="profile.italianOriginCountry"
            :placeholder="$t('form.country.placeholder')"
            :options="{'CH':$t('countries.CH'), 'IT':$t('countries.IT')}"
            @input="$delete(profile, 'italianOriginCity')"
          />
          
          <place-selector
            v-model="profile.italianOriginCity"
            v-if="profile.italianOriginCountry"
            :validation="$v.profile.italianOriginCity"
            :country="profile.italianOriginCountry"
            :placeholder="$t('form.municipality.placeholder')"
            :italianOnly="true"
          />

          <p class="text-secondary_text mt-8 mb-4">{{$t('profile.question.language-context')}}*</p>
          <checks-input 
            otherPlaceholder="Altro"
            v-model="profile.languageContext"
            :validation="$v.profile.languageContext"
            :options="languages"
            :showOptions="5"
            :errorcheckers="{
              required: (validation) => $t('error.form.required')
            }"
          />
        </wizard-page>

      </wizard>

      <div class="mt-8">
        <div class="py-4 text-secondary_text text-sm">
          I campi contrassegnati con l'asterisco (*) sono obbligatori.
        </div>
        <div class="flex" v-if="$refs.wizard">
          <button v-if="allowCancel" class="flex-1 btn btn-2 mr-2" @click="cancel()">{{$t('cancel')}}</button>
          <button v-if="$refs.wizard.isLast" class="flex-1 btn btn-1" @click="save()">{{$t('save')}}</button>
          <button v-else class="flex-1 btn btn-1" @click="$refs.wizard.next()">{{$t('continue')}}</button>
        </div>
      </div>


    </div>

  </div>

</template>

<script>

import countryCodes from '@/lib/countryCodes.json'
import languages from '@/lib/languages.json'

import OtherplacesEditor from '@/components/OtherplacesEditor'
import TextInput from '@/components/TextInput'
import IntegerInput from '@/components/IntegerInput'
import SelectInput from '@/components/SelectInput'
import ChecksInput from '@/components/ChecksInput'
import PlaceSelector from '@/components/PlaceSelector'

import Wizard from "@/components/Wizard";
import WizardPage from "@/components/WizardPage";

import { validationMixin } from 'vuelidate'
import { required, minLength, between, requiredIf, integer } from 'vuelidate/lib/validators'


export default {
  mixins: [validationMixin],

  validations: {
    displayName: {
      required,
      minLength: minLength(3)
    },
    profile: {
      birthyear: {
        required,
        integer,
        between: between(new Date().getFullYear() - 126, new Date().getFullYear() - 6)
      },
      country: {
        required
      },
      residence: {
        required: requiredIf(function () {
          return this.profile.country == 'IT' || this.profile.country == 'CH'
        })
      },
      mainLanguage: {
        required
      },
      placeLanguage: {
        required
      },
      italianOriginCountry: {
        required
      },
      italianOriginCity: {
        required
      },
      languageContext: {
        required
      }
    }
  },


  data() {
    return {
      displayName: this.$store.getters.user.displayName,
      countryCodes,
      languages,
      maxyear: new Date().getFullYear() - 6,
      profile: {
        degree: '',
        country: '',
        places: [],
        otherLanguages: [],
        italianOriginCountry: '',
        italianOriginCity: '',
        languageContext: [],
        residence: {},
      }
    }
  },

  props: {
    allowCancel: Boolean
  },

  components: {
    OtherplacesEditor,
    TextInput,
    IntegerInput,
    SelectInput,
    Wizard,
    WizardPage,
    ChecksInput,
    PlaceSelector
  },


  async mounted() {
    await this.$store.getters.authInit
    this.displayName = this.$store.getters.user.displayName
    
    const prof = this.$store.getters.profile
    if(prof) {
      this.profile = prof
    }
    
  },

  watch: {
    ['profile.country'](n) {
      if(n != 'CH' && n != 'IT') {
        this.$delete(this.profile, 'residence')
      } else if(!this.profile.residence) {
        this.$set(this.profile, 'residence', {})
      }
    }
  },

  methods: {
    cancel() {
      this.$emit('cancelled')
    },
    goBack() {
      this.$refs.wizard.prev()
    },
    async save() {
      if(this.$refs.wizard.isComplete()) {
        if(this.$v.$invalid) {
          alert(this.$t('error.form.must-complete-registration'))
        } else {
          const promises = [
            this.$store.dispatch('persistProfile', this.profile),
            this.$store.dispatch('persistProfilePublic', {
              displayName: this.displayName,
            }),
          ]
          if(this.displayName != this.$store.getters.user.displayName) {
            promises.push(this.$store.dispatch('updateProfile', {displayName: this.displayName}))
          }
          
          await Promise.all(promises)
          this.$emit('persisted')
        }
      } else {
        this.$refs.wizard.touchForErrors()
      }
    }
  },
}
</script>
